// src/core/services/auth/MagicLinkService.ts

import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  query, 
  where, 
  getDocs,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

export interface MagicLinkToken {
  id: string;
  targetId: string;      // SocialProfile handle or ID
  targetType: 'business' | 'lead';
  email?: string;
  expiresAt: Timestamp;
  used: boolean;
  createdAt: Timestamp;
}

const COLLECTION_NAME = 'magicLinks';

export const MagicLinkService = {
  /**
   * Generates a unique token for a specific business/lead.
   */
  async generateToken(targetId: string, targetType: 'business' | 'lead', email?: string): Promise<string> {
    const tokenId = uuidv4();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 48); // 48 hour expiration

    const tokenData: MagicLinkToken = {
      id: tokenId,
      targetId,
      targetType,
      email,
      expiresAt: Timestamp.fromDate(expiresAt),
      used: false,
      createdAt: Timestamp.now()
    };

    await setDoc(doc(db, COLLECTION_NAME, tokenId), tokenData);
    return tokenId;
  },

  /**
   * Verifies if a token is valid, not used, and not expired.
   */
  async verifyToken(tokenId: string): Promise<MagicLinkToken | null> {
    const docRef = doc(db, COLLECTION_NAME, tokenId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;

    const data = docSnap.data() as MagicLinkToken;
    const now = Timestamp.now();

    if (data.used || data.expiresAt.toMillis() < now.toMillis()) {
      return null;
    }

    return data;
  },

  /**
   * Marks a token as used.
   */
  async markAsUsed(tokenId: string): Promise<void> {
    const docRef = doc(db, COLLECTION_NAME, tokenId);
    await setDoc(docRef, { used: true }, { merge: true });
  },

  /**
   * Deletes expired tokens (Maintenance).
   */
  async cleanupExpiredTokens(): Promise<number> {
    const now = Timestamp.now();
    const q = query(collection(db, COLLECTION_NAME), where('expiresAt', '<', now));
    const querySnapshot = await getDocs(q);
    
    let deletedCount = 0;
    for (const tokenDoc of querySnapshot.docs) {
      await deleteDoc(doc(db, COLLECTION_NAME, tokenDoc.id));
      deletedCount++;
    }
    
    return deletedCount;
  }
};
